import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  ArrowDownLeft,
  ArrowUpRight,
  CircleDollarSign,
  FileClock,
  Layers,
  LayoutGrid,
  Menu,
  PackageOpen,
  Receipt,
  RefreshCw,
  Tag,
} from 'lucide-react'
import ClientDashboardV3Layout from './ClientDashboardV3Layout'
import DashboardFilterBar from './components/DashboardFilterBar'
import { KeyMetricsSection } from './components/KeyMetricsSection'
import RetailPerformanceSection from './components/RetailPerformanceSection'
import { TransactionsProfitabilitySection } from './components/TransactionsProfitabilitySection'
import { HealthCashFlowSection } from './components/HealthCashFlowSection'
import { BestPerformersSection } from './components/BestPerformersSection'
import { MerchandiseInsightsSection } from './components/MerchandiseInsightsSection'
export { buildHealthSnapshot, buildCashFlowRows, buildRoiSummaryCards } from './components/HealthCashFlowSection'
export { buildVendorBestPerformersData } from './components/BestPerformersSection'
export { buildTransactionalPrimaryCards, buildTransactionalCompactCards } from './components/TransactionsProfitabilitySection'
export { summarizePerformanceBuyingRows, summarizePerformanceSellThroughRows } from './components/MerchandiseInsightsSection'
import ClientDataService from '../../../services/ClientData.service'
import CurrentClientProvider from '../../../providers/CurrentClientProvider'
import GlobalTip from '../../../providers/GlobalTip'
import EpochMonthService from '../../../services/EpochMonth.service'
import { trackEvent } from '../../../services/AmplitudeService'
import PerformanceDashboardService from '../../../services/dashboards/PerformanceDashboard.service'
import {
  buildDashboardFilterRequestParams,
  createEmptyDashboardFilters,
  DashboardFilterOption,
  getDashboardFilterSelectionLabel,
  serializeDashboardFilters,
} from '../../../services/dashboards/dashboardFilters'
import ReportService from '../../../services/reports/Report.service'
import {
  ClientLevelItem,
} from '../../../interfaces/dashboards/PerformanceDashboard.interface'
import { RawData } from '../../../interfaces/dashboards/KpiDashboard.interface'
import { Switch } from '../../../tailwindComponents/ui/switch'
import { cn } from '../../../lib/utils'

const TABS = [
  'Key metrics',
  'Transactions & profitability',
  'Retail Performance',
  'Health & Cash flow',
  'Best performers',
  'Merchandise Insights',
] as const

const TAB_HEADER_INFO: Record<(typeof TABS)[number], { title: string; subtitle: string }> = {
  'Key metrics': {
    title: 'Key metrics',
    subtitle: 'The five numbers that summarize how your store is doing right now.',
  },
  'Transactions & profitability': {
    title: 'Transactions & Profitability',
    subtitle: "A breakdown of every transaction and what it's actually earning you.",
  },
  'Retail Performance': {
    title: 'Sales Trend',
    subtitle: "How sales are moving over time, and where they're heading.",
  },
  'Health & Cash flow': {
    title: 'Health & Cash flow',
    subtitle: "Your store's financial health — cash position, obligations, and runway.",
  },
  'Best performers': {
    title: 'Best performers',
    subtitle: 'The classes and vendors driving your results this period.',
  },
  'Merchandise Insights': {
    title: 'Merchandise Insights',
    subtitle: 'Buying opportunities and sell-through signals across your classes.',
  },
}

const PERIODS = ['MTD', 'Last Month', 'Last 3M', 'Last 12M'] as const
const PERFORMER_SERIES_COLORS = ['#7F56D9', '#2563EB', '#16A34A', '#EA580C', '#DB2777', '#0891B2', '#CA8A04', '#4F46E5']
const PERFORMANCE_SECTION_ELIGIBLE_POS_IDS = new Set([1200, 1201, 1202, 35])
const PERFORMANCE_TRANSACTIONAL_SECTION_ELIGIBLE_POS_IDS = new Set([1200, 1201, 1202, 35])
const PERFORMANCE_SECTION_LIVE_POS_ID = 1200

type DeltaTone = 'success' | 'destructive' | 'info'
type Period = (typeof PERIODS)[number]
type PerformanceTab = (typeof TABS)[number]
type ClassTab = 'Buying Opportunity' | 'Fastest Sell-Through' | 'Slowest Sell-Through'
type ValueMode = 'Retail' | 'Cost'
type BestPerformersFilter = 'Vendor' | 'Product Type' | 'Items' | 'Vendor > Class'

export function scrollPerformanceSectionIntoView(element: HTMLElement | null) {
  element?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export function resolvePerformanceSectionAvailability(pointOfSaleId: number | string | null | undefined) {
  const hasInputValue = pointOfSaleId !== null && pointOfSaleId !== undefined && pointOfSaleId !== ''
  const numericPointOfSaleId = hasInputValue ? Number(pointOfSaleId) : Number.NaN
  const hasKnownPointOfSaleId = Number.isFinite(numericPointOfSaleId)
  const isEligiblePerformancePos = hasKnownPointOfSaleId && PERFORMANCE_SECTION_ELIGIBLE_POS_IDS.has(numericPointOfSaleId)
  const liveBestPerformers = numericPointOfSaleId === PERFORMANCE_SECTION_LIVE_POS_ID

  return {
    pointOfSaleId: hasKnownPointOfSaleId ? numericPointOfSaleId : null,
    isEligiblePerformancePos,
    transactionsProfitabilityMode: PERFORMANCE_TRANSACTIONAL_SECTION_ELIGIBLE_POS_IDS.has(numericPointOfSaleId) ? 'live' as const : 'comingSoon' as const,
    bestPerformersMode: liveBestPerformers ? 'live' as const : 'comingSoon' as const,
  }
}

export function getRetailPerformanceTrendRequestWindow(epochMonth: number) {
  const resolvedEpochMonth = Number(epochMonth) || 0

  return {
    startEpochMonth: Math.max(resolvedEpochMonth - 12, 0),
    endEpochMonth: resolvedEpochMonth,
  }
}

export interface InventoryHealthRow {
  locationCode?: string | null;
  locationName?: string | null;
  category: string;
  className: string;
  classCode: string;
  salesRetail: number;
  salesPlanRetail?: number;
  markdownsRetail: number;
  inventoryRetail: number;
  inventoryTrendRetail: number;
  inventoryPlanRetail: number;
  onOrderRetail: number;
  onOrderCost: number;
  otbRetail: number;
  otbCost: number;
  buyingOpportunityScore: number;
}

export interface SellThroughRow {
  locationCode?: string | null;
  locationName?: string | null;
  category: string;
  className: string;
  classCode: string;
  sales: number;
  inflowRetail: number;
  beginningInventory: number;
  mmuPercent: number;
  sellThroughRate: number;
  compToLyPercent: number;
  sellThroughGoal: number;
}

interface MetricCard {
  label: string;
  icon: any;
  value: string;
  delta: string;
  deltaDollar: string;
  tone: DeltaTone;
  ly: string;
  trend: MetricTrendPoint[];
  mtdTrend: MetricTrendPoint[];
  trendLabel: string;
  planComparison: string;
  planComparisonDollar: string;
  lastYearComparison: string;
  lastYearComparisonDollar: string;
}

interface MetricTrendPoint {
  month: string;
  current: number;
  comparison: number;
}

function compareNaturalAlphaNumeric(left: string, right: string) {
  return String(left || '').localeCompare(String(right || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

function sortPerformanceOptionsByCodeThenName(options: DashboardFilterOption[]) {
  return [...options].sort((left, right) => {
    const codeComparison = compareNaturalAlphaNumeric(left?.code || '', right?.code || '')
    if (codeComparison !== 0) {
      return codeComparison
    }

    const nameComparison = compareNaturalAlphaNumeric(left?.name || left?.label || '', right?.name || right?.label || '')
    if (nameComparison !== 0) {
      return nameComparison
    }

    return compareNaturalAlphaNumeric(left?.category || '', right?.category || '')
  })
}

export function sortPerformanceLocationOptions(options: DashboardFilterOption[]) {
  return sortPerformanceOptionsByCodeThenName(options)
}

export function sortPerformanceCategoryOptions(options: DashboardFilterOption[]) {
  return sortPerformanceOptionsByCodeThenName(options)
}

export function sortPerformanceClassOptions(options: DashboardFilterOption[]) {
  return sortPerformanceOptionsByCodeThenName(options)
}

interface TrendStat {
  label: string;
  value: string;
  sub: string;
  tone: string;
}

interface PerformerRowSummary {
  key: string;
  locationCode: string;
  classCode: string;
  classLabel: string;
  category: string;
  location: string;
  sales: number;
  salesLabel: string;
  sellThroughLabel: string;
  compToLyLabel: string;
  compToLyUp: boolean;
  goalLabel: string;
  goalDeltaLabel: string;
  goalPositive: boolean;
  mmuLabel: string;
}

export interface KpiSummary {
  sales: number;
  salesPlan: number;
  salesComp: number;
  markdowns: number;
  markdownsPlan: number;
  markdownsComp: number;
  inflowCost: number;
  inflowPlanCost: number;
  inflowCostComp: number;
  inflowRetail: number;
  inflowPlanRetail: number;
  inflowRetailComp: number;
  inventoryCurrent: number;
  inventoryBomAvg: number;
  inventoryBomPlanAvg: number;
  inventoryAvgComp: number;
  inventoryCurrentComp: number;
  rcrAcrossLocations: number;
  rccAcrossLocations: number;
  rcrAcrossLocationsComp: number;
  rccAcrossLocationsComp: number;
  imu: number;
  imuPlan: number;
  imuComp: number;
  mmu: number;
  mmuPlan: number;
  mmuComp: number;
  markdownPct: number;
  markdownPctPlan: number;
  markdownPctComp: number;
  cashMargin: number;
  cashMarginPlan: number;
  cashMarginComp: number;
  cashMarginPct: number;
  cashMarginPctPlan: number;
  cashMarginPctComp: number;
  costOfPurchasesPct: number;
  costOfPurchasesPlanPct: number;
  costOfPurchasesPctComp: number;
  turn: number;
  turnPlan: number;
  turnComp: number;
  efficiency: number;
  efficiencyPlan: number;
  efficiencyComp: number;
  freshness: number;
  freshnessComp: number;
  grossMargin: number;
  grossMarginComp: number;
  grossMarginPct: number;
  grossMarginPctComp: number;
  gmroi: number;
  gmroiComp: number;
  cmroi: number;
  cmroiComp: number;
}

interface ProfitabilityCard {
  label: string;
  icon: any;
  value: string;
  delta: string;
  ly: string;
  toneClassName: string;
}

interface ProfitabilityMiniStat {
  label: string;
  icon: any;
  value: string;
  delta: string;
  deltaClassName: string;
}

export interface TransactionTrendRow {
  monthDate: string;
  transactionCount: number;
  totalUnitsSold: number;
  unitsPerTransaction: number;
  averageOrderValue: number;
  grossMarginPct: number;
  transactionCountLy: number;
  totalUnitsSoldLy: number;
  unitsPerTransactionLy: number;
  averageOrderValueLy: number;
  grossMarginPctLy: number;
}

interface DailySalesTrendRow {
  salesDate: string;
  seriesLabel: string;
  sales: number;
}

export interface BestPerformersApiRow {
  rowType: 'summary' | 'daily';
  performer: string;
  salesTy: number;
  salesLy: number;
  unitsTy: number | null;
  unitsLy: number | null;
  salesDate: string | null;
}

interface LocationOption {
  code: string;
  name: string;
  label: string;
}

export function getPeriodMonthCount(period: Period) {
  if (period === 'MTD') return 1
  if (period === 'Last Month') return 1
  if (period === 'Last 3M') return 3
  return 12
}

export function getPeriodEndEpochMonth(period: Period, epochMonth: number) {
  if (period === 'MTD') return epochMonth
  return Math.max(epochMonth - 1, 0)
}

export function selectPeriodWindowRows(rows: ClientLevelItem[], periodEndEpochMonth: number, periodMonthCount: number) {
  return rows.filter((row) => row.epochMonthSpan <= periodEndEpochMonth).slice(-periodMonthCount)
}

function formatSignedPointDelta(value: number, digits = 1) {
  const prefix = value >= 0 ? '+' : ''
  return `${prefix}${value.toFixed(digits)} pts`
}

function percentageDelta(current: number, compare: number) {
  if (!compare) return 0
  return ((current - compare) / Math.abs(compare)) * 100
}

export function aggregateKpiRows(rows: RawData[]): KpiSummary | null {
  if (!rows.length) return null

  const totals = rows.reduce((acc, row) => ({
    sales: acc.sales + (row.sales || 0),
    salesPlan: acc.salesPlan + (row.salesPlan || 0),
    salesComp: acc.salesComp + (row.salesComp || 0),
    markdowns: acc.markdowns + (row.markdowns || 0),
    markdownsPlan: acc.markdownsPlan + (row.markdownsPlan || 0),
    markdownsComp: acc.markdownsComp + (row.markdownsComp || 0),
    inflowCost: acc.inflowCost + (row.inflowCost || 0),
    inflowPlanCost: acc.inflowPlanCost + (row.inflowPlanCost || 0),
    inflowCostComp: acc.inflowCostComp + (row.inflowCostComp || 0),
    inflowRetail: acc.inflowRetail + (row.inflowRetail || 0),
    inflowPlanRetail: acc.inflowPlanRetail + (row.inflowPlanRetail || 0),
    inflowRetailComp: acc.inflowRetailComp + (row.inflowRetailComp || 0),
    inventoryCurrent: acc.inventoryCurrent + (row.inventoryCurrent || 0),
    inventoryBomAvg: acc.inventoryBomAvg + (row.inventoryBomAvg || 0),
    inventoryBomPlanAvg: acc.inventoryBomPlanAvg + (row.inventoryBomPlanAvg || 0),
    inventoryAvgComp: acc.inventoryAvgComp + (row.inventoryAvgComp || 0),
    inventoryCurrentComp: acc.inventoryCurrentComp + (row.inventoryCurrentComp || 0),
    rcrAcrossLocations: acc.rcrAcrossLocations + (row.rcrAcrossLocations || 0),
    rccAcrossLocations: acc.rccAcrossLocations + (row.rccAcrossLocations || 0),
    rcrAcrossLocationsComp: acc.rcrAcrossLocationsComp + (row.rcrAcrossLocationsComp || 0),
    rccAcrossLocationsComp: acc.rccAcrossLocationsComp + (row.rccAcrossLocationsComp || 0),
  }), {
    sales: 0,
    salesPlan: 0,
    salesComp: 0,
    markdowns: 0,
    markdownsPlan: 0,
    markdownsComp: 0,
    inflowCost: 0,
    inflowPlanCost: 0,
    inflowCostComp: 0,
    inflowRetail: 0,
    inflowPlanRetail: 0,
    inflowRetailComp: 0,
    inventoryCurrent: 0,
    inventoryBomAvg: 0,
    inventoryBomPlanAvg: 0,
    inventoryAvgComp: 0,
    inventoryCurrentComp: 0,
    rcrAcrossLocations: 0,
    rccAcrossLocations: 0,
    rcrAcrossLocationsComp: 0,
    rccAcrossLocationsComp: 0,
  })

  const imu = ReportService.calculateImu({ receivedRetailAcrossLocations: totals.rcrAcrossLocations, receivedCostAcrossLocations: totals.rccAcrossLocations })
  const imuPlan = ReportService.calculateImu({ receivedRetailAcrossLocations: totals.inflowPlanRetail, receivedCostAcrossLocations: totals.inflowPlanCost })
  const imuComp = ReportService.calculateImu({ receivedRetailAcrossLocations: totals.rcrAcrossLocationsComp, receivedCostAcrossLocations: totals.rccAcrossLocationsComp })
  const markdownPct = totals.sales ? totals.markdowns / totals.sales : 0
  const markdownPctPlan = totals.salesPlan ? totals.markdownsPlan / totals.salesPlan : 0
  const markdownPctComp = totals.salesComp ? totals.markdownsComp / totals.salesComp : 0
  const mmu = ReportService.calculateMmu({ receivedRetailAcrossLocations: totals.rcrAcrossLocations, receivedCostAcrossLocations: totals.rccAcrossLocations, markdownPercent: markdownPct })
  const mmuPlan = ReportService.calculateMmu({ receivedRetailAcrossLocations: totals.inflowPlanRetail, receivedCostAcrossLocations: totals.inflowPlanCost, markdownPercent: markdownPctPlan })
  const mmuComp = ReportService.calculateMmu({ receivedRetailAcrossLocations: totals.rcrAcrossLocationsComp, receivedCostAcrossLocations: totals.rccAcrossLocationsComp, markdownPercent: markdownPctComp })
  const cashMargin = ReportService.calculateCashMargin({ sales: totals.sales, inflowCost: totals.inflowCost, format: false }) as number
  const cashMarginPlan = ReportService.calculateCashMargin({ sales: totals.salesPlan, inflowCost: totals.inflowPlanCost, format: false }) as number
  const cashMarginComp = ReportService.calculateCashMargin({ sales: totals.salesComp, inflowCost: totals.inflowCostComp, format: false }) as number
  const costComplement = 1 - imu
  const costComplementComp = 1 - imuComp
  const grossMargin = ReportService.calculateGrossMargin({ sales: totals.sales, markdowns: totals.markdowns, costComplement, imu, format: false }) as number
  const grossMarginComp = ReportService.calculateGrossMargin({ sales: totals.salesComp, markdowns: totals.markdownsComp, costComplement: costComplementComp, imu: imuComp, format: false }) as number

  return {
    ...totals,
    imu,
    imuPlan,
    imuComp,
    mmu,
    mmuPlan,
    mmuComp,
    markdownPct,
    markdownPctPlan,
    markdownPctComp,
    cashMargin,
    cashMarginPlan,
    cashMarginComp,
    cashMarginPct: totals.sales ? (cashMargin / totals.sales) * 100 : 0,
    cashMarginPctPlan: totals.salesPlan ? (cashMarginPlan / totals.salesPlan) * 100 : 0,
    cashMarginPctComp: totals.salesComp ? (cashMarginComp / totals.salesComp) * 100 : 0,
    costOfPurchasesPct: ReportService.calculateCop({ inflowCost: totals.inflowCost, sales: totals.sales }) * 100,
    costOfPurchasesPlanPct: ReportService.calculateCop({ inflowCost: totals.inflowPlanCost, sales: totals.salesPlan }) * 100,
    costOfPurchasesPctComp: ReportService.calculateCop({ inflowCost: totals.inflowCostComp, sales: totals.salesComp }) * 100,
    turn: ReportService.calculateTurn({ sales: totals.sales, invAvg: totals.inventoryBomAvg, format: false }) as number,
    turnPlan: ReportService.calculateTurn({ sales: totals.salesPlan, invAvg: totals.inventoryBomPlanAvg, format: false }) as number,
    turnComp: ReportService.calculateTurn({ sales: totals.salesComp, invAvg: totals.inventoryAvgComp, format: false }) as number,
    efficiency: ReportService.calculateEfficiency({ sales: totals.sales, inventory: totals.inventoryCurrent, inflowRetail: totals.inflowRetail, format: false }) as number,
    efficiencyPlan: ReportService.calculateEfficiency({ sales: totals.salesPlan, inventory: totals.inventoryCurrent, inflowRetail: totals.inflowPlanRetail, format: false }) as number,
    efficiencyComp: ReportService.calculateEfficiency({ sales: totals.salesComp, inventory: totals.inventoryCurrentComp, inflowRetail: totals.inflowRetailComp, format: false }) as number,
    freshness: ReportService.calculateFreshness({ inflowRetail: totals.inflowRetail, inventory: totals.inventoryCurrent, format: false }) as number,
    freshnessComp: ReportService.calculateFreshness({ inflowRetail: totals.inflowRetailComp, inventory: totals.inventoryCurrentComp, format: false }) as number,
    grossMargin,
    grossMarginComp,
    grossMarginPct: totals.sales ? (grossMargin / totals.sales) * 100 : 0,
    grossMarginPctComp: totals.salesComp ? (grossMarginComp / totals.salesComp) * 100 : 0,
    cmroi: ReportService.calculateCmroi({ sales: totals.sales, inflowCost: totals.inflowCost, invAvgCost: totals.inventoryBomAvg * costComplement, format: false }) as number,
    cmroiComp: ReportService.calculateCmroi({ sales: totals.salesComp, inflowCost: totals.inflowCostComp, invAvgCost: totals.inventoryAvgComp * costComplementComp, format: false }) as number,
    gmroi: ReportService.calculateGmroi({ sales: totals.sales, markdowns: totals.markdowns, costComplement, imu, invAvgCost: totals.inventoryBomAvg * costComplement, format: false }) as number,
    gmroiComp: ReportService.calculateGmroi({ sales: totals.salesComp, markdowns: totals.markdownsComp, costComplement: costComplementComp, imu: imuComp, invAvgCost: totals.inventoryAvgComp * costComplementComp, format: false }) as number,
  }
}

export function buildProfitabilityCards(summary: KpiSummary | null): ProfitabilityCard[] {
  if (!summary) return []

  const revenueDelta = percentageDelta(summary.sales, summary.salesComp)
  const grossMarginPctDelta = summary.grossMarginPct - summary.grossMarginPctComp
  const cashMarginPctDelta = summary.cashMarginPct - summary.cashMarginPctComp

  return [
    {
      label: 'Revenue',
      icon: CircleDollarSign,
      value: PerformanceDashboardService.moneyFormat(summary.sales, true),
      delta: `${formatSignedPercent(revenueDelta)} vs LY`,
      ly: `LY ${PerformanceDashboardService.moneyFormat(summary.salesComp, true)}`,
      toneClassName: revenueDelta >= 0 ? 'text-emerald-600' : 'text-red-600',
    },
    {
      label: 'Gross margin',
      icon: ArrowUpRight,
      value: PerformanceDashboardService.moneyFormat(summary.grossMargin, true),
      delta: `${formatSignedPointDelta(grossMarginPctDelta)} vs LY`,
      ly: `${formatPercent(summary.grossMarginPctComp)} of LY sales`,
      toneClassName: grossMarginPctDelta >= 0 ? 'text-emerald-600' : 'text-red-600',
    },
    {
      label: 'Cash margin',
      icon: FileClock,
      value: PerformanceDashboardService.moneyFormat(summary.cashMargin, true),
      delta: `${formatSignedPointDelta(summary.cashMarginPct - summary.cashMarginPctComp)} vs LY`,
      ly: `${formatPercent(summary.cashMarginPctComp)} of LY sales`,
      toneClassName: summary.cashMarginPct >= summary.cashMarginPctComp ? 'text-emerald-600' : 'text-red-600',
    },
  ]
}

export function buildProfitabilityMiniStats(summary: KpiSummary | null): ProfitabilityMiniStat[] {
  if (!summary) return []

  return [
    {
      label: 'Markdown rate',
      icon: Tag,
      value: formatPercent(summary.markdownPct * 100),
      delta: `${formatSignedPointDelta((summary.markdownPct - summary.markdownPctComp) * 100)} vs LY`,
      deltaClassName: summary.markdownPct <= summary.markdownPctComp ? 'text-emerald-600' : 'text-red-600',
    },
    {
      label: 'IMU',
      icon: Layers,
      value: formatPercent(summary.imu * 100),
      delta: `${formatSignedPointDelta((summary.imu - summary.imuComp) * 100)} vs LY`,
      deltaClassName: summary.imu >= summary.imuComp ? 'text-emerald-600' : 'text-red-600',
    },
    {
      label: 'MMU',
      icon: PackageOpen,
      value: formatPercent(summary.mmu * 100),
      delta: `${formatSignedPointDelta((summary.mmu - summary.mmuComp) * 100)} vs LY`,
      deltaClassName: summary.mmu >= summary.mmuComp ? 'text-emerald-600' : 'text-red-600',
    },
    {
      label: 'Turn',
      icon: RefreshCw,
      value: `${summary.turn.toFixed(1)}×`,
      delta: `${summary.turn >= summary.turnComp ? '+' : ''}${(summary.turn - summary.turnComp).toFixed(1)} vs LY`,
      deltaClassName: summary.turn >= summary.turnComp ? 'text-emerald-600' : 'text-red-600',
    },
  ]
}

function formatSignedCurrencyDelta(value: number) {
  const prefix = value >= 0 ? '+' : '-'
  return `${prefix}${PerformanceDashboardService.moneyFormat(Math.abs(value), true)}`
}

function formatSignedPercent(value: number, digits = 1) {
  const prefix = value >= 0 ? '+' : ''
  return `${prefix}${value.toFixed(digits)}%`
}

function formatPercent(value: number, digits = 1) {
  return `${value.toFixed(digits)}%`
}

function calculatePercentDelta(current: number, target: number) {
  if (!target) return 0
  return ((current - target) / Math.abs(target)) * 100
}

// SPS-5171: the Plan/LY comparison tiles need both a dollar and a percent rendering of the same
// "current vs comparison" variance, chosen by the $/% toggle - not a "current/plan" multiplier, which
// can't be expressed as a dollar variance at all and was the reason the toggle only ever changed this tile.
export function formatVariance(current: number, comparison: number, unavailableLabel: string): { percent: string; dollar: string } {
  if (!Number.isFinite(current) || !Number.isFinite(comparison) || comparison === 0) {
    return { percent: unavailableLabel, dollar: unavailableLabel }
  }
  return {
    percent: formatSignedPercent(calculatePercentDelta(current, comparison)),
    dollar: formatSignedCurrencyDelta(current - comparison),
  }
}

function buildMetricTone(delta: number, higherIsBetter = true): DeltaTone {
  if (delta === 0) return 'info'
  if (higherIsBetter) {
    return delta >= 0 ? 'success' : 'destructive'
  }
  return delta <= 0 ? 'success' : 'destructive'
}

function sumClientRows(rows: ClientLevelItem[]) {
  return rows.reduce((acc, row) => ({
    trend: Math.max(acc.trend, row.trend || 0),
    epochMonthSpan: row.epochMonthSpan,
    inventoryBOMPlan: acc.inventoryBOMPlan + (row.inventoryBOMPlan || 0),
    inventoryBOMRM: acc.inventoryBOMRM + (row.inventoryBOMRM || 0),
    inventoryDeviationRm: acc.inventoryDeviationRm + (row.inventoryDeviationRm || 0),
    otbRetailEom: acc.otbRetailEom + (row.otbRetailEom || 0),
    salesPlan: acc.salesPlan + (row.salesPlan || 0),
    salesRm: acc.salesRm + (row.salesRm || 0),
    markdownsRm: acc.markdownsRm + (row.markdownsRm || 0),
    markdownsPlan: acc.markdownsPlan + (row.markdownsPlan || 0),
    inflowRetailRm: acc.inflowRetailRm + (row.inflowRetailRm || 0),
    inflowPlanRetailRm: acc.inflowPlanRetailRm + (row.inflowPlanRetailRm || 0),
    cashMargin: acc.cashMargin + (row.cashMargin || 0),
    cashMarginPlan: acc.cashMarginPlan + (row.cashMarginPlan || 0),
    cashMarginLy: acc.cashMarginLy + (row.cashMarginLy || 0),
    salesLy: acc.salesLy + (row.salesLy || 0),
    markdownsLy: acc.markdownsLy + (row.markdownsLy || 0),
    inventoryBomLy: acc.inventoryBomLy + (row.inventoryBomLy || 0),
    inflowRetailLy: acc.inflowRetailLy + (row.inflowRetailLy || 0),
    inventoryDeviationCostRm: acc.inventoryDeviationCostRm + (row.inventoryDeviationCostRm || 0),
    otbCostEom: acc.otbCostEom + (row.otbCostEom || 0),
    inventoryCurrent: acc.inventoryCurrent + (row.inventoryCurrent || 0),
    inventoryBomPlanNM: acc.inventoryBomPlanNM + (row.inventoryBomPlanNM || 0),
    inventoryEomLy: acc.inventoryEomLy + (row.inventoryEomLy || 0),
    markdownsRemaining: acc.markdownsRemaining + (row.markdownsRemaining || 0),
    onOrderRetailMTD: acc.onOrderRetailMTD + (row.onOrderRetailMTD || 0),
    onOrderCostMTD: acc.onOrderCostMTD + (row.onOrderCostMTD || 0),
    otbRetailMtd: acc.otbRetailMtd + (row.otbRetailMtd || 0),
    otbCostMtd: acc.otbCostMtd + (row.otbCostMtd || 0),
    costComplementMtd: acc.costComplementMtd + (row.costComplementMtd || 0),
    otbLessTrend: (acc.otbLessTrend || 0) + (row.otbLessTrend || 0),
  }), {
    trend: 0,
    epochMonthSpan: rows[rows.length - 1]?.epochMonthSpan || 0,
    inventoryBOMPlan: 0,
    inventoryBOMRM: 0,
    inventoryDeviationRm: 0,
    otbRetailEom: 0,
    salesPlan: 0,
    salesRm: 0,
    markdownsRm: 0,
    markdownsPlan: 0,
    inflowRetailRm: 0,
    inflowPlanRetailRm: 0,
    cashMargin: 0,
    cashMarginPlan: 0,
    cashMarginLy: 0,
    salesLy: 0,
    markdownsLy: 0,
    inventoryBomLy: 0,
    inflowRetailLy: 0,
    inventoryDeviationCostRm: 0,
    otbCostEom: 0,
    inventoryCurrent: 0,
    inventoryBomPlanNM: 0,
    inventoryEomLy: 0,
    markdownsRemaining: 0,
    onOrderRetailMTD: 0,
    onOrderCostMTD: 0,
    otbRetailMtd: 0,
    otbCostMtd: 0,
    costComplementMtd: 0,
    otbLessTrend: 0,
  } as ClientLevelItem)
}

function buildZeroClientLevelItem(epochMonthSpan: number): ClientLevelItem {
  return { ...sumClientRows([]), epochMonthSpan }
}

// The oldest month is always exactly 12 months before currentMonthRow, so its actuals can be read
// from currentMonthRow's own *Ly columns; Plan/OTB have no equivalent column and stay 0.
function recoverOldestMonthFromLastYear(epochMonthSpan: number, currentMonthRow: ClientLevelItem): ClientLevelItem {
  return {
    ...buildZeroClientLevelItem(epochMonthSpan),
    salesRm: currentMonthRow.salesLy || 0,
    markdownsRm: currentMonthRow.markdownsLy || 0,
    inventoryBOMRM: currentMonthRow.inventoryBomLy || 0,
    inflowRetailRm: currentMonthRow.inflowRetailLy || 0,
  }
}

// One row per calendar month in range, even if the backend is missing one, so a gap can't shrink the
// window and shift every other month over.
export function fillMonthlyRowGaps(rows: ClientLevelItem[], startEpochMonth: number, endEpochMonth: number, currentMonthRow: ClientLevelItem | null = null): ClientLevelItem[] {
  const rowsByEpochMonth = new Map(rows.map((row) => [row.epochMonthSpan, row]))
  const filled: ClientLevelItem[] = []

  for (let epochMonth = startEpochMonth; epochMonth <= endEpochMonth; epochMonth += 1) {
    const existing = rowsByEpochMonth.get(epochMonth)
    if (existing) {
      filled.push(existing)
    } else if (epochMonth === startEpochMonth && currentMonthRow) {
      filled.push(recoverOldestMonthFromLastYear(epochMonth, currentMonthRow))
    } else {
      filled.push(buildZeroClientLevelItem(epochMonth))
    }
  }

  return filled
}

export function buildPerformanceKeyMetrics(windowSummary: ClientLevelItem | null, monthlyRows: ClientLevelItem[] = [], kpiSummary: KpiSummary | null = null, windowRows: ClientLevelItem[] = [], currentMonthRow: ClientLevelItem | null = null): MetricCard[] {
  if (!windowSummary) return []

  const trendRows = monthlyRows.slice(-12)
  const annualTurnRows = trendRows.length > 0 ? trendRows : monthlyRows
  const buildTrend = (
    currentValue: (row: ClientLevelItem) => number,
    comparisonValue: (row: ClientLevelItem) => number,
  ): MetricTrendPoint[] => trendRows.map((row) => ({
    month: Number.isFinite(Number(row.epochMonthSpan)) ? EpochMonthService.epochMonthToMMM(row.epochMonthSpan) : '',
    current: Number(currentValue(row)) || 0,
    comparison: Number(comparisonValue(row)) || 0,
  }))
  // Separate from trendRows/buildTrend above, which only cover completed months.
  const buildMtdPoint = (
    currentValue: (row: ClientLevelItem) => number,
    comparisonValue: (row: ClientLevelItem) => number,
  ): MetricTrendPoint[] => {
    if (!currentMonthRow) return []

    return [{
      month: Number.isFinite(Number(currentMonthRow.epochMonthSpan)) ? EpochMonthService.epochMonthToMMM(currentMonthRow.epochMonthSpan) : '',
      current: Number(currentValue(currentMonthRow)) || 0,
      comparison: Number(comparisonValue(currentMonthRow)) || 0,
    }]
  }

  const averageWindowValue = (selector: (row: ClientLevelItem) => number, fallback: number) => {
    if (windowRows.length === 0) {
      return fallback
    }

    return windowRows.reduce((sum, row) => sum + (Number(selector(row)) || 0), 0) / windowRows.length
  }

  const inventoryCurrentValue = averageWindowValue((row) => row.inventoryBOMRM, Number(windowSummary.inventoryBOMRM) || 0)
  const inventoryPlanValue = averageWindowValue((row) => row.inventoryBOMPlan, Number(windowSummary.inventoryBOMPlan) || 0)
  const inventoryLastYearValue = averageWindowValue((row) => row.inventoryBomLy, Number(windowSummary.inventoryBomLy) || 0)

  const salesDelta = calculatePercentDelta(windowSummary.salesRm || 0, windowSummary.salesPlan || 0)
  const inventoryDelta = calculatePercentDelta(inventoryCurrentValue, inventoryPlanValue)
  const markdownDelta = calculatePercentDelta(windowSummary.markdownsRm || 0, windowSummary.markdownsPlan || 0)
  const inflowDelta = calculatePercentDelta(windowSummary.inflowRetailRm || 0, windowSummary.inflowPlanRetailRm || 0)
  const salesPlanVariance = formatVariance(windowSummary.salesRm || 0, windowSummary.salesPlan || 0, 'Plan unavailable')
  const salesLastYearVariance = formatVariance(windowSummary.salesRm || 0, windowSummary.salesLy || 0, 'LY unavailable')
  const inventoryPlanVariance = formatVariance(inventoryCurrentValue, inventoryPlanValue, 'Plan unavailable')
  const inventoryLastYearVariance = formatVariance(inventoryCurrentValue, inventoryLastYearValue, 'LY unavailable')
  const markdownsPlanVariance = formatVariance(windowSummary.markdownsRm || 0, windowSummary.markdownsPlan || 0, 'Plan unavailable')
  const markdownsLastYearVariance = formatVariance(windowSummary.markdownsRm || 0, windowSummary.markdownsLy || 0, 'LY unavailable')
  const inflowPlanVariance = formatVariance(windowSummary.inflowRetailRm || 0, windowSummary.inflowPlanRetailRm || 0, 'Plan unavailable')
  const inflowLastYearVariance = formatVariance(windowSummary.inflowRetailRm || 0, windowSummary.inflowRetailLy || 0, 'LY unavailable')
  const annualTurnSales = annualTurnRows.reduce((sum, row) => sum + (Number(row.salesRm) || 0), 0)
  const annualTurnPlanSales = annualTurnRows.reduce((sum, row) => sum + (Number(row.salesPlan) || 0), 0)
  const annualTurnLastYearSales = annualTurnRows.reduce((sum, row) => sum + (Number(row.salesLy) || 0), 0)
  const annualTurnInventoryAverage = annualTurnRows.length > 0
    ? annualTurnRows.reduce((sum, row) => sum + (Number(row.inventoryBOMRM) || 0), 0) / annualTurnRows.length
    : Number(kpiSummary?.inventoryBomAvg) || 0
  const annualTurnPlanInventoryAverage = annualTurnRows.length > 0
    ? annualTurnRows.reduce((sum, row) => sum + (Number(row.inventoryBOMPlan) || 0), 0) / annualTurnRows.length
    : Number(kpiSummary?.inventoryBomPlanAvg) || 0
  const annualTurnLastYearInventoryAverage = annualTurnRows.length > 0
    ? annualTurnRows.reduce((sum, row) => sum + (Number(row.inventoryBomLy) || 0), 0) / annualTurnRows.length
    : Number(kpiSummary?.inventoryAvgComp) || 0
  const turn = annualTurnRows.length > 0
    ? Number(ReportService.calculateTurn({ sales: annualTurnSales, invAvg: annualTurnInventoryAverage, format: false })) || 0
    : Number(kpiSummary?.turn) || 0
  const turnPlan = annualTurnRows.length > 0
    ? Number(ReportService.calculateTurn({ sales: annualTurnPlanSales, invAvg: annualTurnPlanInventoryAverage, format: false })) || 0
    : Number(kpiSummary?.turnPlan) || 0
  const turnComp = annualTurnRows.length > 0
    ? Number(ReportService.calculateTurn({ sales: annualTurnLastYearSales, invAvg: annualTurnLastYearInventoryAverage, format: false })) || 0
    : Number(kpiSummary?.turnComp) || 0
  const turnDelta = turn - turnPlan

  return [
    {
      label: 'Sales',
      icon: CircleDollarSign,
      value: PerformanceDashboardService.moneyFormat(windowSummary.salesRm || 0, true),
      delta: `${formatSignedPercent(salesDelta)} vs Plan`,
      deltaDollar: `${formatSignedCurrencyDelta((windowSummary.salesRm || 0) - (windowSummary.salesPlan || 0))} vs Plan`,
      tone: buildMetricTone(salesDelta, true),
      ly: `LY ${PerformanceDashboardService.moneyFormat(windowSummary.salesLy || 0, true)}`,
      trend: buildTrend((row) => row.salesRm, (row) => row.salesLy),
      mtdTrend: buildMtdPoint((row) => row.salesRm, (row) => row.salesLy),
      trendLabel: 'vs last year',
      planComparison: salesPlanVariance.percent,
      planComparisonDollar: salesPlanVariance.dollar,
      lastYearComparison: salesLastYearVariance.percent,
      lastYearComparisonDollar: salesLastYearVariance.dollar,
    },
    {
      label: 'Inventory at retail BOM',
      icon: PackageOpen,
      value: PerformanceDashboardService.moneyFormat(inventoryCurrentValue, true),
      delta: `${formatSignedPercent(inventoryDelta)} vs Plan`,
      deltaDollar: `${formatSignedCurrencyDelta(inventoryCurrentValue - inventoryPlanValue)} vs Plan`,
      tone: buildMetricTone(inventoryDelta, false),
      ly: `LY ${PerformanceDashboardService.moneyFormat(inventoryLastYearValue, true)}`,
      trend: buildTrend((row) => row.inventoryBOMRM, (row) => row.inventoryBomLy),
      mtdTrend: buildMtdPoint((row) => row.inventoryBOMRM, (row) => row.inventoryBomLy),
      trendLabel: 'vs last year',
      planComparison: inventoryPlanVariance.percent,
      planComparisonDollar: inventoryPlanVariance.dollar,
      lastYearComparison: inventoryLastYearVariance.percent,
      lastYearComparisonDollar: inventoryLastYearVariance.dollar,
    },
    {
      label: 'Annual Turn',
      icon: RefreshCw,
      value: `${turn.toFixed(1)}x`,
      // Turn has no dollar denomination - the $/% toggle doesn't apply, so both variants are identical.
      delta: `${turnDelta >= 0 ? '+' : ''}${turnDelta.toFixed(1)} vs Plan`,
      deltaDollar: `${turnDelta >= 0 ? '+' : ''}${turnDelta.toFixed(1)} vs Plan`,
      tone: buildMetricTone(turnDelta, true),
      ly: `LY ${turnComp.toFixed(1)}`,
      trend: [],
      mtdTrend: [],
      trendLabel: 'vs last year',
      planComparison: `${turnPlan.toFixed(1)}x`,
      planComparisonDollar: `${turnPlan.toFixed(1)}x`,
      lastYearComparison: `${turnComp.toFixed(1)}x`,
      lastYearComparisonDollar: `${turnComp.toFixed(1)}x`,
    },
    {
      label: 'Markdowns',
      icon: Receipt,
      value: PerformanceDashboardService.moneyFormat(windowSummary.markdownsRm || 0, true),
      delta: `${formatSignedPercent(markdownDelta)} vs Plan`,
      deltaDollar: `${formatSignedCurrencyDelta((windowSummary.markdownsRm || 0) - (windowSummary.markdownsPlan || 0))} vs Plan`,
      tone: buildMetricTone(markdownDelta, false),
      ly: `LY ${PerformanceDashboardService.moneyFormat(windowSummary.markdownsLy || 0, true)}`,
      trend: buildTrend((row) => row.markdownsRm, (row) => row.markdownsLy),
      mtdTrend: buildMtdPoint((row) => row.markdownsRm, (row) => row.markdownsLy),
      trendLabel: 'vs last year',
      planComparison: markdownsPlanVariance.percent,
      planComparisonDollar: markdownsPlanVariance.dollar,
      lastYearComparison: markdownsLastYearVariance.percent,
      lastYearComparisonDollar: markdownsLastYearVariance.dollar,
    },
    {
      label: 'Inflow',
      icon: ArrowDownLeft,
      value: PerformanceDashboardService.moneyFormat(windowSummary.inflowRetailRm || 0, true),
      delta: `${formatSignedPercent(inflowDelta)} vs Plan`,
      deltaDollar: `${formatSignedCurrencyDelta((windowSummary.inflowRetailRm || 0) - (windowSummary.inflowPlanRetailRm || 0))} vs Plan`,
      tone: buildMetricTone(inflowDelta, true),
      ly: `LY ${PerformanceDashboardService.moneyFormat(windowSummary.inflowRetailLy || 0, true)}`,
      trend: buildTrend((row) => row.inflowRetailRm, (row) => row.inflowRetailLy),
      mtdTrend: buildMtdPoint((row) => row.inflowRetailRm, (row) => row.inflowRetailLy),
      trendLabel: 'vs last year',
      planComparison: inflowPlanVariance.percent,
      planComparisonDollar: inflowPlanVariance.dollar,
      lastYearComparison: inflowLastYearVariance.percent,
      lastYearComparisonDollar: inflowLastYearVariance.dollar,
    },
  ]
}

export function getSelectedPerformanceMetric(metrics: MetricCard[], label: string | null): MetricCard | null {
  return metrics.find((metric) => metric.label === label) || null
}

export function buildPerformanceTargetStats(currentMonth: ClientLevelItem | null): TrendStat[] {
  if (!currentMonth) return []

  const salesTarget = currentMonth.salesPlan || 0
  const actualSales = currentMonth.salesRm || 0
  const runRateEom = currentMonth.trend ? actualSales / currentMonth.trend : actualSales
  const planProjectionEom = salesTarget
  const pyShapeEom = currentMonth.trend ? (currentMonth.salesLy || 0) / currentMonth.trend : (currentMonth.salesLy || 0)
  const totalShortfall = Math.max(salesTarget - actualSales, 0)

  return [
    {
      label: 'Monthly Target',
      value: PerformanceDashboardService.moneyFormat(salesTarget, true),
      sub: EpochMonthService.epochMonthToMMMM(currentMonth.epochMonthSpan),
      tone: 'text-gray-500',
    },
    {
      label: 'Actual to date',
      value: PerformanceDashboardService.moneyFormat(actualSales, true),
      sub: `${formatSignedCurrencyDelta(actualSales - salesTarget)} vs plan`,
      tone: actualSales >= salesTarget ? 'text-emerald-600' : 'text-red-600',
    },
    {
      label: 'Run-rate EOM',
      value: PerformanceDashboardService.moneyFormat(runRateEom, true),
      sub: `${formatSignedCurrencyDelta(runRateEom - salesTarget)} vs target`,
      tone: runRateEom >= salesTarget ? 'text-emerald-600' : 'text-red-600',
    },
    {
      label: 'Plan-Proj EOM',
      value: PerformanceDashboardService.moneyFormat(planProjectionEom, true),
      sub: `${formatSignedCurrencyDelta(planProjectionEom - salesTarget)} vs target`,
      tone: planProjectionEom >= salesTarget ? 'text-emerald-600' : 'text-red-600',
    },
    {
      label: 'PY-Shape EOM',
      value: PerformanceDashboardService.moneyFormat(pyShapeEom, true),
      sub: `${formatSignedCurrencyDelta(pyShapeEom - salesTarget)} vs target`,
      tone: pyShapeEom >= salesTarget ? 'text-emerald-600' : 'text-red-600',
    },
    {
      label: 'Total shortfall',
      value: PerformanceDashboardService.moneyFormat(totalShortfall, true),
      sub: totalShortfall > 0 ? 'Distributed by current plan weight' : 'No shortfall in the loaded month',
      tone: totalShortfall > 0 ? 'text-red-700' : 'text-emerald-600',
    },
  ]
}

export function buildPerformanceChartData(rows: ClientLevelItem[], count: number) {
  return rows.slice(-count).map((row) => ({
    date: EpochMonthService.epochMonthToMMM(row.epochMonthSpan),
    actual: Math.round(row.salesRm || 0),
    plan: Math.round(row.salesPlan || 0),
    runRateProjection: row.trend ? Math.round((row.salesRm || 0) / row.trend) : null,
    planProjection: Math.round(row.salesPlan || 0),
    requiredToHitTarget: Math.round(Math.max((row.salesPlan || 0) - (row.salesRm || 0), 0)),
    pyShapeProjection: row.trend ? Math.round((row.salesLy || 0) / row.trend) : null,
  }))
}

export function summarizePerformanceBestPerformers(rows: SellThroughRow[], limit = 8): PerformerRowSummary[] {
  return rows.slice(0, limit).map((row) => {
    const goalDelta = (row.sellThroughRate || 0) - (row.sellThroughGoal || 0)
    const compToLy = row.compToLyPercent || 0
    const sales = Number(row.sales) || 0

    return {
      key: `${row.locationCode || 'all'}-${row.classCode}`,
      locationCode: row.locationCode || '',
      classCode: row.classCode,
      classLabel: `${row.classCode} - ${row.className}`,
      category: row.category,
      location: row.locationName || row.locationCode || 'All locations',
      sales,
      salesLabel: PerformanceDashboardService.moneyFormat(sales, true),
      sellThroughLabel: formatPercent(row.sellThroughRate || 0),
      compToLyLabel: formatSignedPercent(compToLy),
      compToLyUp: compToLy >= 0,
      goalLabel: formatPercent(row.sellThroughGoal || 0),
      goalDeltaLabel: `${formatSignedPercent(goalDelta)} vs goal`,
      goalPositive: goalDelta >= 0,
      mmuLabel: formatPercent((row.mmuPercent || 0) * 100),
    }
  })
}

export function getPerformanceBestPerformerSeriesLabel(row: Pick<PerformerRowSummary, 'classLabel' | 'location' | 'category'>) {
  return `${row.classLabel} · ${row.location} · ${row.category}`
}

export function buildPerformanceDailySalesChartData(rows: DailySalesTrendRow[]) {
  const labels = Array.from(new Set(rows.map((row) => row.seriesLabel)))
  const series = labels.map((label, index) => ({ key: `series${index}`, label, color: PERFORMER_SERIES_COLORS[index % PERFORMER_SERIES_COLORS.length] }))
  const seriesKeyByLabel = new Map(series.map((item) => [item.label, item.key]))
  const pointsByDate = new Map<string, Record<string, string | number>>()

  rows.forEach((row) => {
    const point = pointsByDate.get(row.salesDate) || {
      date: new Date(`${row.salesDate}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
    }
    const seriesKey = seriesKeyByLabel.get(row.seriesLabel)
    if (seriesKey) point[seriesKey] = Number(row.sales) || 0
    pointsByDate.set(row.salesDate, point)
  })

  return { series, data: Array.from(pointsByDate.entries()).sort(([left], [right]) => left.localeCompare(right)).map(([, point]) => point) }
}

export const DEFAULT_SELECTED_KEY_METRIC_LABEL = 'Inventory at retail BOM'

export default function ClientDashboardV3Performance() {
  const location = useLocation()
  const [tabbed, setTabbed] = useState(true)
  const [activeTab, setActiveTab] = useState<(typeof TABS)[number]>(() => {
    const requestedTab = (location.state as any)?.activeTab
    return requestedTab && (TABS as readonly string[]).includes(requestedTab) ? (requestedTab as any) : 'Key metrics'
  })

  React.useEffect(() => {
    const requestedTab = (location.state as any)?.activeTab
    if (requestedTab && (TABS as readonly string[]).includes(requestedTab)) {
      setActiveTab(requestedTab as any)
    }
  }, [location.state])
  const [selectedKeyMetricLabel, setSelectedKeyMetricLabel] = useState<string | null>(DEFAULT_SELECTED_KEY_METRIC_LABEL)
  const [activePeriod, setActivePeriod] = useState<Period>('Last 12M')
  const currentClientContext = CurrentClientProvider.useClient() as any
  const globalTip = GlobalTip.useMessage() as any
  const currentClient = currentClientContext?.client
  const performanceSectionAvailability = React.useMemo(
    () => resolvePerformanceSectionAvailability(currentClient?.PointOfSaleId),
    [currentClient?.PointOfSaleId],
  )
  const showLiveTransactionsProfitability = performanceSectionAvailability.transactionsProfitabilityMode === 'live'
  const showLiveBestPerformers = performanceSectionAvailability.bestPerformersMode === 'live'
  const visibleTabs = React.useMemo(
    () => TABS.filter((tab) => {
      if (tab === 'Transactions & profitability' || tab === 'Best performers') {
        return performanceSectionAvailability.isEligiblePerformancePos
      }

      return true
    }),
    [performanceSectionAvailability.isEligiblePerformancePos],
  )
  const [clientLevelData, setClientLevelData] = React.useState<ClientLevelItem[]>([])
  const [keyMetricTrendData, setKeyMetricTrendData] = React.useState<ClientLevelItem[]>([])
  const [transactionTrendData, setTransactionTrendData] = React.useState<TransactionTrendRow[]>([])
  const [bestPerformersData, setBestPerformersData] = React.useState<BestPerformersApiRow[]>([])
  const [bestPerformersFilter, setBestPerformersFilter] = React.useState<BestPerformersFilter>('Vendor')
  const [bestPerformersRange, setBestPerformersRange] = React.useState<string>('Last 12M')
  const [bestPerformersLimit, setBestPerformersLimit] = React.useState<number>(10)
  const [locationOptions, setLocationOptions] = React.useState<DashboardFilterOption[]>([])
  const [categoryOptions, setCategoryOptions] = React.useState<DashboardFilterOption[]>([])
  const [classOptions, setClassOptions] = React.useState<DashboardFilterOption[]>([])
  const [dashboardFilters, setDashboardFilters] = React.useState(() => createEmptyDashboardFilters())
  const [merchandiseInsightsTableLimit, setMerchandiseInsightsTableLimit] = React.useState(5)
  const [merchandiseInsightsBeginningPeriod, setMerchandiseInsightsBeginningPeriod] = React.useState(Math.max(EpochMonthService.getTodayEpochMonth() - 11, 0))
  const [inventoryHealthData, setInventoryHealthData] = React.useState<InventoryHealthRow[]>([])
  const [fastestSellThroughData, setFastestSellThroughData] = React.useState<SellThroughRow[]>([])
  const [slowestSellThroughData, setSlowestSellThroughData] = React.useState<SellThroughRow[]>([])
  const [kpiRawData, setKpiRawData] = React.useState<RawData[]>([])
  const [loading, setLoading] = React.useState(true)
  const [keyMetricTrendLoading, setKeyMetricTrendLoading] = React.useState(true)
  const [transactionTrendLoading, setTransactionTrendLoading] = React.useState(true)
  const [bestPerformersLoading, setBestPerformersLoading] = React.useState(true)
  const [insightsLoading, setInsightsLoading] = React.useState(true)
  const [kpiLoading, setKpiLoading] = React.useState(true)
  const sectionRefs = React.useRef<Partial<Record<PerformanceTab, HTMLElement | null>>>({})

  const epochMonth = EpochMonthService.getTodayEpochMonth()
  const compareEpochMonth = epochMonth - 1
  const merchandiseInsightsEndingPeriod = epochMonth
  const periodEndEpochMonth = getPeriodEndEpochMonth(activePeriod, epochMonth)
  const periodMonthCount = getPeriodMonthCount(activePeriod)
  const beginningPeriod = Math.max(periodEndEpochMonth - (periodMonthCount - 1), 0)
  const dashboardFilterParams = React.useMemo(() => buildDashboardFilterRequestParams(dashboardFilters), [dashboardFilters])
  const dashboardFilterKey = React.useMemo(() => serializeDashboardFilters(dashboardFilters), [dashboardFilters])
  const selectedLocations = dashboardFilterParams.locations
  const selectedCategories = dashboardFilterParams.categories
  const selectedClasses = dashboardFilterParams.classes
  const resolvedClientCode = currentClient?.ClientCode || 'none'
  const trackPerformanceInteraction = React.useCallback((action: string, payload: Record<string, unknown> = {}) => {
    trackEvent('V3 Performance Event', {
      action,
      clientCode: resolvedClientCode,
      ...payload,
    })
  }, [resolvedClientCode])
  const selectedLocationLabel = React.useMemo(
    () => getDashboardFilterSelectionLabel(dashboardFilters.location, locationOptions, 'All locations', 'locations'),
    [dashboardFilters.location, locationOptions],
  )
  const merchandiseInsightsBeginningPeriodOptions = React.useMemo(() => {
    const options: Array<{ value: number; label: string }> = []

    for (let offset = 11; offset >= 0; offset -= 1) {
      const optionEpochMonth = Math.max(epochMonth - offset, 0)
      options.push({
        value: optionEpochMonth,
        label: `${EpochMonthService.epochMonthToMMMM(optionEpochMonth)} ${EpochMonthService.epochMonthToDate(optionEpochMonth).getUTCFullYear()}`,
      })
    }

    return options
  }, [epochMonth])
  const merchandiseInsightsEndingPeriodLabel = React.useMemo(
    () => `${EpochMonthService.epochMonthToMMMM(merchandiseInsightsEndingPeriod)} ${EpochMonthService.epochMonthToDate(merchandiseInsightsEndingPeriod).getUTCFullYear()}`,
    [merchandiseInsightsEndingPeriod],
  )
  const isVisible = (tab: PerformanceTab) => visibleTabs.includes(tab) && (!tabbed || activeTab === tab)
  const handleTabClick = (tab: PerformanceTab) => {
    setActiveTab(tab)
    trackPerformanceInteraction('Section Tab Clicked', {
      sectionTab: tab,
      tabbed,
    })

    if (!tabbed) {
      window.requestAnimationFrame(() => scrollPerformanceSectionIntoView(sectionRefs.current[tab] ?? null))
    }
  }
  const handleDashboardFiltersChange = React.useCallback((nextFilters: ReturnType<typeof createEmptyDashboardFilters>) => {
    setDashboardFilters(nextFilters)
    trackPerformanceInteraction('Dashboard Filters Changed', {
      filterKey: serializeDashboardFilters(nextFilters),
    })
  }, [trackPerformanceInteraction])
  const handleSelectedKeyMetricLabelChange = React.useCallback((nextLabel: string | null) => {
    setSelectedKeyMetricLabel(nextLabel)
    trackPerformanceInteraction('Key Metric Changed', {
      keyMetricLabel: nextLabel || '',
    })
  }, [trackPerformanceInteraction])
  const handleActivePeriodChange = React.useCallback((nextPeriod: Period) => {
    setActivePeriod(nextPeriod)
    trackPerformanceInteraction('Period Changed', {
      period: nextPeriod,
    })
  }, [trackPerformanceInteraction])
  const handleBestPerformersFilterChange = React.useCallback((nextFilter: BestPerformersFilter) => {
    setBestPerformersFilter(nextFilter)
    trackPerformanceInteraction('Best Performers Filter Changed', {
      bestPerformersFilter: nextFilter,
    })
  }, [trackPerformanceInteraction])
  const handleMerchandiseInsightsTableLimitChange = React.useCallback((nextLimit: number) => {
    setMerchandiseInsightsTableLimit(nextLimit)
    trackPerformanceInteraction('Merchandise Insights Table Limit Changed', {
      tableLimit: nextLimit,
    })
  }, [trackPerformanceInteraction])
  const handleMerchandiseInsightsBeginningPeriodChange = React.useCallback((nextPeriod: number) => {
    setMerchandiseInsightsBeginningPeriod(nextPeriod)
    trackPerformanceInteraction('Merchandise Insights Period Changed', {
      beginningEpochMonth: nextPeriod,
    })
  }, [trackPerformanceInteraction])

  React.useEffect(() => {
    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0])
    }
  }, [activeTab, visibleTabs])

  React.useEffect(() => {
    if (!currentClient?.Id) {
      setClientLevelData([])
      setLoading(false)
      return
    }

    setLoading(true)
    PerformanceDashboardService.getSimpleDashboardDataForClient({
      clientId: currentClient.Id,
      epochMonth,
      compareEpochMonth,
      locations: selectedLocations,
      categories: selectedCategories,
      classes: selectedClasses,
      locationExclude: dashboardFilterParams.locationExclude,
      categoryExclude: dashboardFilterParams.categoryExclude,
      classExclude: dashboardFilterParams.classExclude,
    }).then((data) => {
      setClientLevelData(Array.isArray(data?.client) ? data.client : [])
      setLoading(false)
    }).catch((error) => {
      console.error(error)
      globalTip.show('Failed to fetch performance data')
      setClientLevelData([])
      setLoading(false)
    })
  }, [compareEpochMonth, currentClient?.Id, dashboardFilterKey, dashboardFilterParams.categoryExclude, dashboardFilterParams.classExclude, dashboardFilterParams.locationExclude, epochMonth, globalTip])

  React.useEffect(() => {
    let cancelled = false

    if (!currentClient?.Id) {
      setLocationOptions([])
      setCategoryOptions([])
      setClassOptions([])
      return () => {
        cancelled = true
      }
    }

    PerformanceDashboardService.getDropdownOptions({ clientId: currentClient.Id, epochMonth })
      .then((data) => {
        if (cancelled) return
        const locations = Array.isArray(data?.locations) ? data.locations as LocationOption[] : []
        setLocationOptions(sortPerformanceLocationOptions(locations))
        setCategoryOptions(sortPerformanceCategoryOptions(Array.isArray(data?.categories) ? data.categories as DashboardFilterOption[] : []))
        setClassOptions(sortPerformanceClassOptions(Array.isArray(data?.classes) ? data.classes as DashboardFilterOption[] : []))
      })
      .catch((error) => {
        if (cancelled) return
        console.error(error)
        globalTip.show('Failed to fetch Performance filters')
        setLocationOptions([])
        setCategoryOptions([])
        setClassOptions([])
      })

    return () => {
      cancelled = true
    }
  }, [currentClient?.Id, epochMonth, globalTip])

  React.useEffect(() => {
    setDashboardFilters(createEmptyDashboardFilters())
    setMerchandiseInsightsBeginningPeriod(Math.max(epochMonth - 11, 0))
  }, [currentClient?.Id, epochMonth])

  React.useEffect(() => {
    let cancelled = false

    if (!currentClient?.Id) {
      setKeyMetricTrendData([])
      setKeyMetricTrendLoading(false)
      return () => {
        cancelled = true
      }
    }

    setKeyMetricTrendLoading(true)
    const retailPerformanceTrendWindow = getRetailPerformanceTrendRequestWindow(epochMonth)
    PerformanceDashboardService.getPerformanceDashboardKeyMetricTrendData({
      clientId: currentClient.Id,
      startEpochMonth: retailPerformanceTrendWindow.startEpochMonth,
      endEpochMonth: retailPerformanceTrendWindow.endEpochMonth,
      locations: selectedLocations,
      categories: selectedCategories,
      classes: selectedClasses,
      locationExclude: dashboardFilterParams.locationExclude,
      categoryExclude: dashboardFilterParams.categoryExclude,
      classExclude: dashboardFilterParams.classExclude,
    }).then((data) => {
      if (cancelled) return
      setKeyMetricTrendData(Array.isArray(data) ? data : [])
      setKeyMetricTrendLoading(false)
    }).catch((error) => {
      if (cancelled) return
      console.error(error)
      globalTip.show('Failed to fetch historical KPI data')
      setKeyMetricTrendData([])
      setKeyMetricTrendLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [currentClient?.Id, dashboardFilterKey, dashboardFilterParams.categoryExclude, dashboardFilterParams.classExclude, dashboardFilterParams.locationExclude, epochMonth, globalTip])

  React.useEffect(() => {
    let cancelled = false

    if (!currentClient?.Id || !showLiveTransactionsProfitability) {
      setTransactionTrendData([])
      setTransactionTrendLoading(false)
      return () => {
        cancelled = true
      }
    }

    setTransactionTrendLoading(true)
    PerformanceDashboardService.getPerformanceDashboardTransactionTrendData({
      clientId: currentClient.Id,
      startEpochMonth: epochMonth - 5,
      endEpochMonth: epochMonth,
    }).then((data) => {
      if (cancelled) return
      setTransactionTrendData(Array.isArray(data) ? data as TransactionTrendRow[] : [])
      setTransactionTrendLoading(false)
    }).catch((error) => {
      if (cancelled) return
      console.error(error)
      globalTip.show('Failed to fetch Shopify transaction trend data')
      setTransactionTrendData([])
      setTransactionTrendLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [currentClient?.Id, epochMonth, globalTip, showLiveTransactionsProfitability])

  React.useEffect(() => {
    let cancelled = false

    if (!currentClient?.Id) {
      setKpiRawData([])
      setKpiLoading(false)
      return () => {
        cancelled = true
      }
    }

    setKpiLoading(true)
    ClientDataService.getClientKpiData({
      clientId: currentClient.Id,
      startEpochMonth: beginningPeriod,
      endEpochMonth: periodEndEpochMonth,
      compareToYearsAgo: 1,
    }).then((response) => {
      if (cancelled) return
      setKpiRawData(Array.isArray(response?.data) ? response.data : [])
      setKpiLoading(false)
    }).catch((error) => {
      if (cancelled) return
      console.error(error)
      globalTip.show('Failed to fetch KPI snapshot data')
      setKpiRawData([])
      setKpiLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [beginningPeriod, currentClient?.Id, globalTip, periodEndEpochMonth])

  React.useEffect(() => {
    let cancelled = false

    if (!currentClient?.Id) {
      setInventoryHealthData([])
      setFastestSellThroughData([])
      setSlowestSellThroughData([])
      setInsightsLoading(false)
      return () => {
        cancelled = true
      }
    }

    const fetchStartTime = Date.now()
    setInsightsLoading(true)

    Promise.allSettled([
      PerformanceDashboardService.getInventoryHealthData({
        clientId: currentClient.Id,
        epochMonth,
        beginningPeriod: merchandiseInsightsBeginningPeriod,
        compareEpochMonth,
        locations: selectedLocations,
        categories: selectedCategories,
        classes: selectedClasses,
        locationExclude: dashboardFilterParams.locationExclude,
        categoryExclude: dashboardFilterParams.categoryExclude,
        classExclude: dashboardFilterParams.classExclude,
        limit: merchandiseInsightsTableLimit,
        endingPeriod: merchandiseInsightsEndingPeriod,
      }),
      PerformanceDashboardService.getFastestSellThroughData({
        clientId: currentClient.Id,
        epochMonth,
        beginningPeriod: merchandiseInsightsBeginningPeriod,
        compareEpochMonth: epochMonth - 12,
        locations: selectedLocations,
        categories: selectedCategories,
        classes: selectedClasses,
        locationExclude: dashboardFilterParams.locationExclude,
        categoryExclude: dashboardFilterParams.categoryExclude,
        classExclude: dashboardFilterParams.classExclude,
        limit: merchandiseInsightsTableLimit,
      }),
      PerformanceDashboardService.getSlowestSellThroughData({
        clientId: currentClient.Id,
        epochMonth,
        beginningPeriod: merchandiseInsightsBeginningPeriod,
        compareEpochMonth: epochMonth - 12,
        locations: selectedLocations,
        categories: selectedCategories,
        classes: selectedClasses,
        locationExclude: dashboardFilterParams.locationExclude,
        categoryExclude: dashboardFilterParams.categoryExclude,
        classExclude: dashboardFilterParams.classExclude,
        limit: merchandiseInsightsTableLimit,
      }),
    ]).then(([buyingResult, fastestResult, slowestResult]) => {
      if (cancelled) return

      const elapsed = Date.now() - fetchStartTime
      const remainingDelay = Math.max(350 - elapsed, 0)

      setTimeout(() => {
        if (cancelled) return
        setInventoryHealthData(buyingResult.status === 'fulfilled' && Array.isArray(buyingResult.value) ? buyingResult.value as InventoryHealthRow[] : [])
        setFastestSellThroughData(fastestResult.status === 'fulfilled' && Array.isArray(fastestResult.value) ? fastestResult.value as SellThroughRow[] : [])
        setSlowestSellThroughData(slowestResult.status === 'fulfilled' && Array.isArray(slowestResult.value) ? slowestResult.value as SellThroughRow[] : [])
        setInsightsLoading(false)
      }, remainingDelay)
    })

    return () => {
      cancelled = true
    }
  }, [compareEpochMonth, currentClient?.Id, dashboardFilterKey, dashboardFilterParams.categoryExclude, dashboardFilterParams.classExclude, dashboardFilterParams.locationExclude, epochMonth, merchandiseInsightsBeginningPeriod, merchandiseInsightsEndingPeriod, merchandiseInsightsTableLimit])

  const monthlyRows = React.useMemo(() => [...clientLevelData].sort((a, b) => a.epochMonthSpan - b.epochMonthSpan), [clientLevelData])
  const keyMetricTrendRows = React.useMemo(() => [...keyMetricTrendData].sort((a, b) => a.epochMonthSpan - b.epochMonthSpan), [keyMetricTrendData])
  const currentMonthRow = React.useMemo(
    () => keyMetricTrendRows.find((row) => row.epochMonthSpan === epochMonth) || null,
    [keyMetricTrendRows, epochMonth],
  )
  // Always the 12 most recently completed months, independent of activePeriod - the current month is
  // shown separately via mtdTrend.
  const completedTrendRows = React.useMemo(
    () => fillMonthlyRowGaps(keyMetricTrendRows, Math.max(compareEpochMonth - 11, 0), Math.max(compareEpochMonth, 0), currentMonthRow),
    [keyMetricTrendRows, compareEpochMonth, currentMonthRow],
  )
  const transactionTrendRows = React.useMemo(() => [...transactionTrendData].sort((a, b) => a.monthDate.localeCompare(b.monthDate)), [transactionTrendData])
  const windowRows = React.useMemo(
    () => selectPeriodWindowRows(keyMetricTrendRows, periodEndEpochMonth, periodMonthCount),
    [keyMetricTrendRows, periodEndEpochMonth, periodMonthCount],
  )
  const windowSummary = React.useMemo(() => windowRows.length ? sumClientRows(windowRows) : null, [windowRows])
  const kpiSummary = React.useMemo(() => aggregateKpiRows(kpiRawData), [kpiRawData])

  const keyMetrics = React.useMemo(() => buildPerformanceKeyMetrics(windowSummary, completedTrendRows, kpiSummary, windowRows, currentMonthRow), [completedTrendRows, currentMonthRow, kpiSummary, windowRows, windowSummary])
  const selectedKeyMetric = React.useMemo(
    () => getSelectedPerformanceMetric(keyMetrics, selectedKeyMetricLabel),
    [keyMetrics, selectedKeyMetricLabel],
  )
  const titleLabel = monthlyRows.length > 0 ? EpochMonthService.epochMonthToMMMYY(monthlyRows[monthlyRows.length - 1].epochMonthSpan) : 'Current period'
  const periodLabel = activePeriod === 'MTD' ? titleLabel : activePeriod

  React.useEffect(() => {
    let cancelled = false

    if (!currentClient?.Id || !showLiveBestPerformers) {
      setBestPerformersData([])
      setBestPerformersLoading(false)
      return () => {
        cancelled = true
      }
    }

    const bestPerformersTargetEpochMonth = bestPerformersRange === 'Last Month' ? Math.max(epochMonth - 1, 0) : epochMonth

    setBestPerformersLoading(true)
    PerformanceDashboardService.getPerformanceDashboardBestPerformersData({
      clientId: currentClient.Id,
      epochMonth: bestPerformersTargetEpochMonth,
      groupBy: bestPerformersFilter,
      range: bestPerformersRange,
      locations: selectedLocations,
      categories: selectedCategories,
      classes: selectedClasses,
      locationExclude: dashboardFilterParams.locationExclude,
      categoryExclude: dashboardFilterParams.categoryExclude,
      classExclude: dashboardFilterParams.classExclude,
      limit: bestPerformersLimit,
    }).then((data) => {
      if (cancelled) return
      setBestPerformersData(Array.isArray(data) ? data as BestPerformersApiRow[] : [])
      setBestPerformersLoading(false)
    }).catch((error) => {
      if (cancelled) return
      console.error(error)
      globalTip.show('Failed to fetch Best Performers data')
      setBestPerformersData([])
      setBestPerformersLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [bestPerformersFilter, bestPerformersLimit, bestPerformersRange, currentClient?.Id, dashboardFilterKey, dashboardFilterParams.categoryExclude, dashboardFilterParams.classExclude, dashboardFilterParams.locationExclude, epochMonth, globalTip, selectedCategories, selectedClasses, selectedLocations, showLiveBestPerformers])

  return (
    <ClientDashboardV3Layout activeTab="performance">
      <div className="flex w-full flex-col gap-3 pb-24">
        <DashboardFilterBar
          filters={dashboardFilters}
          locationOptions={locationOptions}
          categoryOptions={categoryOptions}
          classOptions={classOptions}
          disabled={!currentClient?.Id || ['Transactions & profitability', 'Health & Cash flow'].includes(activeTab)}
          disabledTooltip={['Transactions & profitability', 'Health & Cash flow'].includes(activeTab) ? "This section reflects your full store filters aren't applied here, so your Health Score always means the same thing." : undefined}
          fullBleed
          className="-mt-6"
          onChange={handleDashboardFiltersChange}
        />

        {(() => {
          const activeHeaderInfo = TAB_HEADER_INFO[activeTab] || {
            title: 'Key metrics',
            subtitle: 'The five numbers that summarize how your store is doing right now.',
          }

          return (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">{activeHeaderInfo.title}</h1>
                <p className="mt-0 max-w-4xl text-sm leading-relaxed text-[#667085]">
                  {activeHeaderInfo.subtitle}
                </p>
              </div>

              <div className="inline-flex items-center rounded-lg bg-[#F2F4F7] p-1 gap-1 border border-[#EAECF0] self-start sm:self-auto">
                <button
                  type="button"
                  onClick={() => {
                    if (!tabbed) {
                      setTabbed(true)
                      trackPerformanceInteraction('Tabbed Mode Changed', { tabbed: true })
                    }
                  }}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs transition-all cursor-pointer',
                    tabbed
                      ? 'bg-white text-[#344054] shadow-xs font-semibold'
                      : 'text-[#667085] hover:text-[#344054] font-medium'
                  )}
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                  Tabbed
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (tabbed) {
                      setTabbed(false)
                      trackPerformanceInteraction('Tabbed Mode Changed', { tabbed: false })
                    }
                  }}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs transition-all cursor-pointer',
                    !tabbed
                      ? 'bg-white text-[#344054] shadow-xs font-semibold'
                      : 'text-[#667085] hover:text-[#344054] font-medium'
                  )}
                >
                  <Menu className="h-3.5 w-3.5" />
                  Scroll
                </button>
              </div>
            </div>
          )
        })()}

        {!currentClient?.Id && !loading && (
          <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-sm text-gray-500">
            Select a client to load the performance dashboard.
          </section>
        )}
        <div className="-mx-4 flex flex-col gap-2 rounded-xl bg-white border-b border-[#EAECF0] px-4 sm:mx-0 sm:flex-row sm:items-center sm:justify-between sm:px-0 mt-1">
          <nav className="-mb-px flex items-center gap-6 overflow-x-auto whitespace-nowrap px-4 sm:px-2">
            {visibleTabs.map((tab) => {
              const displayLabel = tab === 'Retail Performance' ? 'Sales Trend' : tab
              const isActive = activeTab === tab
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => handleTabClick(tab)}
                  className={cn(
                    'relative flex h-[42px] items-center justify-center px-0.5 text-xs transition-all cursor-pointer',
                    isActive
                      ? 'text-[#5925DC] font-bold'
                      : 'text-[#667085] hover:text-[#344054] font-medium',
                  )}
                >
                  {displayLabel}
                  {isActive && (
                    <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#7F56D9]" />
                  )}
                </button>
              )
            })}
          </nav>
        </div>

        <div className="flex flex-col gap-10">
          {isVisible('Key metrics') && (
            <div ref={(element) => { sectionRefs.current['Key metrics'] = element }} className="flex scroll-mt-6 flex-col gap-9">
              <KeyMetricsSection
                metrics={keyMetrics}
                selectedMetric={selectedKeyMetric}
                selectedLabel={selectedKeyMetricLabel}
                onSelectedLabelChange={handleSelectedKeyMetricLabelChange}
                activePeriod={activePeriod}
                onActivePeriodChange={handleActivePeriodChange}
                loading={loading}
                trendLoading={keyMetricTrendLoading}
              />
            </div>
          )}

          {isVisible('Transactions & profitability') && (
            <div ref={(element) => { sectionRefs.current['Transactions & profitability'] = element }} className="flex scroll-mt-6 flex-col gap-6">
              <TransactionsProfitabilitySection rows={transactionTrendRows} summary={kpiSummary} loading={kpiLoading || transactionTrendLoading} comingSoon={!showLiveTransactionsProfitability} />
            </div>
          )}

          {isVisible('Retail Performance') && (
            <div ref={(element) => { sectionRefs.current['Retail Performance'] = element }} className="scroll-mt-6">
              <RetailPerformanceSection
                monthlyRows={keyMetricTrendRows}
                loading={keyMetricTrendLoading}
                locationLabel={selectedLocationLabel}
              />
            </div>
          )}
          {isVisible('Health & Cash flow') && (
            <div ref={(element) => { sectionRefs.current['Health & Cash flow'] = element }} className="scroll-mt-6">
              <HealthCashFlowSection />
            </div>
          )}
          {isVisible('Best performers') && (
            <div ref={(element) => { sectionRefs.current['Best performers'] = element }} className="scroll-mt-6">
              <BestPerformersSection
                rows={bestPerformersData}
                loading={bestPerformersLoading}
                filter={bestPerformersFilter}
                onFilterChange={handleBestPerformersFilterChange}
                range={bestPerformersRange}
                onRangeChange={(r) => setBestPerformersRange(r)}
                limit={bestPerformersLimit}
                onLimitChange={(l) => setBestPerformersLimit(l)}
                comingSoon={!showLiveBestPerformers}
              />
            </div>
          )}
          {isVisible('Merchandise Insights') && (
            <div ref={(element) => { sectionRefs.current['Merchandise Insights'] = element }} className="scroll-mt-6">
              <MerchandiseInsightsSection
                loading={insightsLoading}
                inventoryHealthData={inventoryHealthData}
                fastestSellThroughData={fastestSellThroughData}
                slowestSellThroughData={slowestSellThroughData}
                tableLimit={merchandiseInsightsTableLimit}
                onTableLimitChange={handleMerchandiseInsightsTableLimitChange}
                beginningPeriod={merchandiseInsightsBeginningPeriod}
                onBeginningPeriodChange={handleMerchandiseInsightsBeginningPeriodChange}
                beginningPeriodOptions={merchandiseInsightsBeginningPeriodOptions}
                endingPeriodLabel={merchandiseInsightsEndingPeriodLabel}
                periodLabel={periodLabel}
              />
            </div>
          )}
        </div>
      </div>
    </ClientDashboardV3Layout>
  )
}
